import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";

const LOADING_QUOTES = [
  "rebellionStarted",
  "RebelsBreakRules",
  "LookingForLostEntrepreneurs",
  "inner-innovator",
  "PopcornAnyone",
  "GotAnIdea",
];

const ball = keyframes`
  0%   { transform: translate(0, 0); }
  5%   { transform: translate(8px, -14px); }
  10%  { transform: translate(15px, -10px); }
  17%  { transform: translate(23px, -24px); }
  20%  { transform: translate(30px, -20px); }
  27%  { transform: translate(38px, -34px); }
  30%  { transform: translate(45px, -30px); }
  37%  { transform: translate(53px, -44px); }
  40%  { transform: translate(60px, -40px); }
  50%  { transform: translate(60px, 0); }
  57%  { transform: translate(53px, -14px); }
  60%  { transform: translate(45px, -10px); }
  67%  { transform: translate(37px, -24px); }
  70%  { transform: translate(30px, -20px); }
  77%  { transform: translate(22px, -34px); }
  80%  { transform: translate(15px, -30px); }
  87%  { transform: translate(7px, -44px); }
  90%  { transform: translate(0, -40px); }
  100% { transform: translate(0, 0); }
`;

// Each bar rests at a different height and swaps with its mirror partner
// halfway through the cycle, so the ball always lands on a bar top.
const barUp = (rest: number, peak: number) => keyframes`
  0%   { transform: scale(1, ${rest}); }
  40%  { transform: scale(1, ${rest}); }
  50%  { transform: scale(1, ${peak}); }
  90%  { transform: scale(1, ${peak}); }
  100% { transform: scale(1, ${rest}); }
`;

const BARS = [
  barUp(0.2, 1),
  barUp(0.4, 0.8),
  barUp(0.6, 0.6),
  barUp(0.8, 0.4),
  barUp(1, 0.2),
];

interface LoadingComponentProps {
  showMessage?: boolean;
}

const LoadingComponent = ({ showMessage = true }: LoadingComponentProps) => {
  const { t } = useTranslation(["translation.utils"]);

  // Picked once per mount. The Sass-era version called Math.random() inline in
  // the JSX, so the quote changed on every re-render while still loading.
  const quote = useMemo(
    () => LOADING_QUOTES[Math.floor(Math.random() * LOADING_QUOTES.length)],
    []
  );

  return (
    <div>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          minWidth: 200,
          minHeight: 100,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ position: "relative", width: 75, height: 100 }}>
          {BARS.map((animation, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              sx={{
                position: "absolute",
                bottom: 0,
                width: 10,
                height: "50%",
                bgcolor: "info.50",
                transformOrigin: "center bottom",
                boxShadow: "1px 1px 0 rgba(0, 0, 0, .2)",
                left: i * 15,
                transform: `scale(1, ${(i + 1) * 0.2})`,
                animation: `${animation} 4s infinite`,
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 0,
              width: 10,
              height: 10,
              bgcolor: "info.50",
              borderRadius: "50%",
              animation: `${ball} 4s infinite`,
            }}
          />
        </Box>
      </Box>
      {showMessage && (
        <Typography
          sx={{
            display: "block",
            width: "100%",
            textAlign: "center",
            mt: "10px",
            fontSize: "1.2em",
            fontStyle: "italic",
          }}
        >
          {t(quote)}
        </Typography>
      )}
    </div>
  );
};

export default LoadingComponent;
